import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookUser, Plus, Save, Trash2, Search, X, Upload, AlertTriangle, Loader2 } from 'lucide-react';
import { useTenant } from '../components/TenantContext.jsx';
import { directoryApi } from '../api/calls.js';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';
import { ImportDirectoryDialog, ImportJobIndicator } from '../components/Directory/ImportDirectoryDialog.jsx';
import { useDirectoryImportJob } from '../hooks/useDirectoryImportJob.js';
import { overlayDismiss } from '../lib/overlayDismiss';

const EMPTY = {
    employeeId: '',
    name: '',
    department: '',
    title: '',
    ext: '',
    services: [],
    aliases: [],
    enabled: true,
};

function sortByExt(rows) {
    return [...rows].sort((a, b) => {
        const ai = parseInt(a.ext, 10);
        const bi = parseInt(b.ext, 10);
        if (Number.isNaN(ai) && Number.isNaN(bi)) return String(a.ext).localeCompare(String(b.ext));
        if (Number.isNaN(ai)) return 1;
        if (Number.isNaN(bi)) return -1;
        return ai - bi;
    });
}

export default function DirectoryPage() {
    const { t } = useTranslation('directory');
    const { hostId } = useTenant();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState('');
    const [togglingId, setTogglingId] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    // 通訊錄筆數上限（後端 DIRECTORY_MAX_ENTRIES，隨 /list 回傳；後端擋新增前先讓用戶看到用量）
    const [limit, setLimit] = useState(null);
    const [clearOpen, setClearOpen] = useState(false);
    const [clearing, setClearing] = useState(false);
    // 開啟編輯卡時用來捲動定位（編輯卡固定渲染在表格上方，名單長時點下方列的「編輯」
    // 卡會開在畫面外看似「打不開」，靠 openSeq 每次開啟 +1 觸發 scrollIntoView）
    const [openSeq, setOpenSeq] = useState(0);
    const editorRef = useRef(null);
    const importJob = useDirectoryImportJob(hostId);

    // 只在「開啟/切換」編輯卡時捲動，不在編輯欄位打字（onChange 直接 setEditing）時捲
    function openEditor(next) {
        setEditing(next);
        setOpenSeq((n) => n + 1);
    }

    useEffect(() => {
        if (openSeq > 0) {
            editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [openSeq]);

    /**
     * @param {boolean} silent  true 時不顯示 loading state（保留既有 table 直接覆蓋資料，不閃掉）
     */
    async function load(silent = false) {
        if (!hostId) return;
        if (!silent) setLoading(true);
        try {
            const r = await directoryApi.list(hostId);
            setList(r.data || []);
            if (r.limit) setLimit(r.limit);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            if (!silent) setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    // 匯入完成 → silent 刷新 list（不閃 loading state，table 直接顯示新資料）
    useEffect(() => {
        if (importJob.job?.status === 'done') {
            load(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importJob.job?.status, importJob.job?.id]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((r) => {
            const fields = [
                r.name, r.department, r.title, r.ext, r.employeeId,
                ...(Array.isArray(r.aliases) ? r.aliases : []),
                ...(Array.isArray(r.services) ? r.services : []),
            ];
            return fields.some((f) => f && String(f).toLowerCase().includes(q));
        });
    }, [list, search]);
    const sorted = useMemo(() => sortByExt(filtered), [filtered]);

    async function save() {
        if (!editing) return;
        if (!editing.name?.trim()) { alert(t('validation.nameRequired')); return; }
        if (!String(editing.ext || '').trim()) { alert(t('validation.extRequired')); return; }
        const draft = {
            ...editing,
            name: editing.name.trim(),
            ext: String(editing.ext).trim(),
            employeeId: editing.employeeId?.trim() || null,
            department: editing.department?.trim() || null,
            title: editing.title?.trim() || null,
        };
        // Optimistic：立刻關 editor + 本地更新列表
        const prevList = list;
        const prevEditing = editing;
        const tempId = draft.id || `__pending_${Date.now()}`;
        setList((cur) => {
            if (draft.id) {
                return cur.map((c) => (c.id === draft.id ? { ...c, ...draft } : c));
            }
            return [{ ...draft, id: tempId, _pending: true }, ...cur];
        });
        setEditing(null);
        try {
            const r = await directoryApi.upsert({ hostId, ...draft });
            // 拿 server 真實 doc 取代 optimistic（特別是新增時拿 firestore id）
            setList((cur) => cur.map((c) => (c.id === tempId || c.id === draft.id ? r.data : c)));
        } catch (e) {
            // rollback
            setList(prevList);
            setEditing(prevEditing);
            alert(t('error.saveFailed', { message: e.message }));
        }
    }

    async function toggleEnabled(c) {
        setTogglingId(c.id);
        const newEnabled = !c.enabled;
        // Optimistic：立刻 toggle，不等 server
        setList((cur) => cur.map((x) => x.id === c.id ? { ...x, enabled: newEnabled } : x));
        try {
            const r = await directoryApi.upsert({ hostId, id: c.id, ...c, enabled: newEnabled });
            // 用 server 回傳的最新版（含 updatedAt）取代
            if (r?.data) {
                setList((cur) => cur.map((x) => x.id === c.id ? r.data : x));
            }
        } catch (e) {
            // rollback
            setList((cur) => cur.map((x) => x.id === c.id ? { ...x, enabled: c.enabled } : x));
            alert(t('error.toggleFailed', { message: e.message }));
        } finally {
            setTogglingId(null);
        }
    }

    async function remove(c) {
        if (!confirm(t('dialog.deleteConfirm', { name: c.name, ext: c.ext }))) return;
        // Optimistic：先從 list 拿掉，不顯示 loading（避免整張表閃掉）
        const prev = list;
        setList((cur) => cur.filter((x) => x.id !== c.id));
        try {
            await directoryApi.remove(c.id, hostId);
        } catch (e) {
            // rollback
            setList(prev);
            alert(t('error.deleteFailed', { message: e.message }));
        }
    }

    async function clearAll() {
        setClearing(true);
        const prev = list;
        try {
            await directoryApi.clearAll(hostId);
            setList([]);
            setClearOpen(false);
        } catch (e) {
            setList(prev);
            alert(t('error.clearFailed', { message: e.message }));
        } finally {
            setClearing(false);
        }
    }

    return (
        <div className="max-w-6xl">
            <PageHeader
                eyebrow={`tenant · ${hostId}`}
                title={t('page.title')}
                description={
                    limit
                        ? `${t('page.description', { count: list.length })} · ${list.length} / ${limit}`
                        : t('page.description', { count: list.length })
                }
                actions={
                    <>
                        {limit && list.length >= limit * 0.9 && (
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                                list.length >= limit
                                    ? 'bg-danger-100/60 text-danger-600'
                                    : 'bg-warning-100/60 text-warning-600'
                            }`}>
                                <AlertTriangle size={12} />
                                {list.length >= limit
                                    ? t('page.limitReached', { limit })
                                    : t('page.limitNear', { limit })}
                            </span>
                        )}
                        {list.length > 0 && (
                            <Button variant="ghost" onClick={() => setClearOpen(true)}
                                className="text-danger-500 hover:bg-danger-100/50">
                                <Trash2 size={14} /> {t('page.clearAll')}
                            </Button>
                        )}
                        <Button variant="ghost" onClick={() => openEditor({ ...EMPTY })}
                            disabled={!!limit && list.length >= limit}
                            title={limit && list.length >= limit ? t('page.limitReached', { limit }) : undefined}>
                            <Plus size={14} /> {t('page.addMember')}
                        </Button>
                        <Button variant="accent" onClick={() => setImportOpen(true)}>
                            <Upload size={14} /> {t('common:actions.import')}
                        </Button>
                    </>
                }
            />

            <ClearAllDialog
                open={clearOpen}
                count={list.length}
                clearing={clearing}
                onClose={() => !clearing && setClearOpen(false)}
                onConfirm={clearAll}
            />

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {/* Search bar */}
            <Card className="mb-6">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('search.placeholder')}
                        className="w-full pl-9 pr-9 py-2 bg-cream-50 border border-cream-300 rounded-md text-sm text-ink-900 focus:outline-none focus:border-accent-500"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-700"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {search && (
                    <div className="text-xs text-ink-400 mt-2">
                        {t('search.results', { found: sorted.length, total: list.length })}
                    </div>
                )}
            </Card>

            {editing && (
                <div ref={editorRef} style={{ scrollMarginTop: '1rem' }}>
                    <ContactEditor
                        contact={editing}
                        onChange={setEditing}
                        onSave={save}
                        onCancel={() => setEditing(null)}
                    />
                </div>
            )}

            {loading ? (
                <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>
            ) : sorted.length === 0 ? (
                <Card>
                    <Empty
                        icon={BookUser}
                        title={search ? t('empty.noMatchTitle') : t('empty.title')}
                        description={search ? t('empty.noMatchDescription') : t('empty.description')}
                        action={
                            !search && (
                                <Button variant="accent" onClick={() => openEditor({ ...EMPTY })}>
                                    <Plus size={14} /> {t('empty.addContact')}
                                </Button>
                            )
                        }
                    />
                </Card>
            ) : (
                <Card padded={false}>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                                <th className="text-left px-4 py-2.5 font-medium w-24">{t('table.employeeId')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('table.nameAliases')}</th>
                                <th className="text-left px-4 py-2.5 font-medium w-32">{t('table.department')}</th>
                                <th className="text-left px-4 py-2.5 font-medium w-28">{t('table.title')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('table.keywords')}</th>
                                <th className="text-left px-4 py-2.5 font-medium w-20">{t('table.ext')}</th>
                                <th className="text-left px-4 py-2.5 font-medium w-20">{t('table.enabled')}</th>
                                <th className="w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((c) => {
                                const aliases = Array.isArray(c.aliases) ? c.aliases : [];
                                const services = Array.isArray(c.services) ? c.services : [];
                                const enabled = c.enabled !== false;
                                return (
                                    <tr
                                        key={c.id}
                                        className={[
                                            'border-t border-cream-100 hover:bg-cream-50',
                                            !enabled ? 'opacity-50' : '',
                                        ].join(' ')}
                                    >
                                        <td className="px-4 py-3 align-top text-xs font-mono text-ink-500">
                                            {c.employeeId || '—'}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="text-sm font-medium text-ink-900">{c.name}</div>
                                            {aliases.length > 0 && (
                                                <div className="mono-caption">{t('table.aliasesPrefix', { aliases: aliases.join('、') })}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top text-sm text-ink-700">{c.department || '—'}</td>
                                        <td className="px-4 py-3 align-top text-sm text-ink-700">{c.title || '—'}</td>
                                        <td className="px-4 py-3 align-top">
                                            {services.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {services.map((s, i) => (
                                                        <span key={s + i} className="px-1.5 py-0.5 bg-cream-100 border border-cream-200 rounded text-[10px] text-ink-700">
                                                            {s}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-ink-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top text-sm font-mono tnum text-accent-700">
                                            {c.ext}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <button
                                                onClick={() => toggleEnabled(c)}
                                                disabled={togglingId === c.id}
                                                className={[
                                                    'relative inline-flex w-9 h-5 rounded-full transition shrink-0',
                                                    enabled ? 'bg-accent-500' : 'bg-cream-300',
                                                    togglingId === c.id ? 'opacity-50' : '',
                                                ].join(' ')}
                                            >
                                                <span
                                                    className={[
                                                        'absolute top-0.5 inline-block size-4 bg-white rounded-full transition shadow-sm',
                                                        enabled ? 'left-4' : 'left-0.5',
                                                    ].join(' ')}
                                                />
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 align-top text-right space-x-1">
                                            <Button size="sm" variant="ghost" onClick={() => openEditor({ ...c, services: [...services], aliases: [...aliases] })}>
                                                {t('common:actions.edit')}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(c)}>
                                                <Trash2 size={12} className="text-danger-500" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            <ImportDirectoryDialog
                open={importOpen}
                hostId={hostId}
                job={importJob.job}
                loading={importJob.loading}
                existingDirectory={list}
                onClose={() => setImportOpen(false)}
                onStart={importJob.start}
                onConfirm={async (contacts) => {
                    await importJob.confirm(contacts);
                    await load();
                }}
                onDismiss={async () => {
                    await importJob.dismiss();
                    setImportOpen(false);
                }}
                onDone={async () => {
                    await load();
                    await importJob.dismiss();
                }}
            />
            {!importOpen && (
                <ImportJobIndicator
                    job={importJob.job}
                    onClick={() => setImportOpen(true)}
                />
            )}
        </div>
    );
}

function ContactEditor({ contact, onChange, onSave, onCancel }) {
    const { t } = useTranslation('directory');
    const isEdit = !!contact.id;
    return (
        <Card className="mb-6 ring-2 ring-accent-300">
            <SectionHeader title={isEdit ? t('editor.editTitle') : t('editor.addTitle')}>
                <div className="space-x-2">
                    <Button variant="ghost" onClick={onCancel}>{t('common:actions.cancel')}</Button>
                    <Button variant="accent" onClick={onSave}>
                        <Save size={14} /> {t('common:actions.save')}
                    </Button>
                </div>
            </SectionHeader>
            <div className="grid grid-cols-2 gap-4">
                <Input
                    label={t('editor.employeeIdLabel')}
                    value={contact.employeeId || ''}
                    onChange={(e) => onChange({ ...contact, employeeId: e.target.value })}
                    placeholder="A0001"
                    mono
                />
                <Input
                    label={t('editor.nameLabel')}
                    value={contact.name}
                    onChange={(e) => onChange({ ...contact, name: e.target.value })}
                    required
                />
                <Input
                    label={t('editor.extLabel')}
                    value={contact.ext}
                    onChange={(e) => onChange({ ...contact, ext: e.target.value.replace(/[^0-9]/g, '') })}
                    mono
                    required
                />
                <Input
                    label={t('editor.departmentLabel')}
                    value={contact.department || ''}
                    onChange={(e) => onChange({ ...contact, department: e.target.value })}
                />
                <Input
                    label={t('editor.titleLabel')}
                    value={contact.title || ''}
                    onChange={(e) => onChange({ ...contact, title: e.target.value })}
                />
                <Input
                    label={t('editor.enabledLabel')}
                    value={contact.enabled !== false ? t('status.enabled') : t('status.disabled')}
                    disabled
                    hint={t('editor.enabledHint')}
                />
                <div className="col-span-2">
                    <Input
                        label={t('editor.keywordsLabel')}
                        placeholder={t('editor.keywordsPlaceholder')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                const v = e.currentTarget.value.trim();
                                if (v && !(contact.services || []).includes(v)) {
                                    onChange({ ...contact, services: [...(contact.services || []), v] });
                                }
                                e.currentTarget.value = '';
                            }
                        }}
                        hint={t('editor.keywordsHint')}
                    />
                    {(contact.services || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {contact.services.map((s, i) => (
                                <span key={s + i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-cream-100 border border-cream-300 rounded text-xs">
                                    {s}
                                    <button onClick={() => onChange({ ...contact, services: contact.services.filter((_, j) => j !== i) })} className="text-ink-400 hover:text-danger-500">×</button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="col-span-2">
                    <Input
                        label={t('editor.aliasesLabel')}
                        placeholder={t('editor.aliasesPlaceholder')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                const v = e.currentTarget.value.trim();
                                if (v && !(contact.aliases || []).includes(v)) {
                                    onChange({ ...contact, aliases: [...(contact.aliases || []), v] });
                                }
                                e.currentTarget.value = '';
                            }
                        }}
                        hint={t('editor.aliasesHint')}
                    />
                    {(contact.aliases || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {contact.aliases.map((s, i) => (
                                <span key={s + i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-cream-100 border border-cream-300 rounded text-xs">
                                    {s}
                                    <button onClick={() => onChange({ ...contact, aliases: contact.aliases.filter((_, j) => j !== i) })} className="text-ink-400 hover:text-danger-500">×</button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

function ClearAllDialog({ open, count, clearing, onClose, onConfirm }) {
    const { t } = useTranslation('directory');
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] bg-ink-900/40 backdrop-blur-sm grid place-items-center px-4"
            {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 rounded-2xl shadow-[0_20px_60px_-20px_rgba(28,26,23,0.35)] w-full max-w-md overflow-hidden"
                onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-5 flex items-start gap-3.5">
                    <span className="size-10 inline-flex items-center justify-center rounded-xl bg-danger-100/70 text-danger-500 shrink-0">
                        <AlertTriangle size={20} />
                    </span>
                    <div className="min-w-0">
                        <h3 className="heading-display text-lg font-semibold text-ink-900">{t('clearDialog.title')}</h3>
                        <p className="text-[13px] text-ink-600 mt-1 leading-relaxed">
                            {t('clearDialog.message', { count })}
                        </p>
                        <p className="text-[12px] text-danger-500 mt-1.5">{t('clearDialog.warning')}</p>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-cream-200 bg-white flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} disabled={clearing}>{t('common:actions.cancel')}</Button>
                    <Button onClick={onConfirm} disabled={clearing}
                        className="bg-danger-500 text-white hover:bg-danger-600 border-danger-500">
                        {clearing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {clearing ? t('clearDialog.clearing') : t('clearDialog.confirm', { count })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
